import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Download, Lock } from "lucide-react";

const COPPER = "#B5793F";

/* ---------- küçük yardımcılar ---------- */
const Eyebrow = ({ children }) => (
  <div className="font-mono text-[10px] tracking-[0.32em] uppercase" style={{ color: COPPER }}>
    {children}
  </div>
);

const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`font-serif text-[28px] md:text-[34px] leading-[1.1] text-[#16130f] ${className}`}>
    {children}
  </h2>
);

const Sheet = ({ page, total, children, cover = false }) => (
  <section className="report-sheet">
    {!cover && (
      <header className="report-avoid-break flex items-center justify-between border-b border-[#16130f]/10 pb-3">
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#16130f]">
          Ertan Kasap
        </span>
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#16130f]/45">
          Dijital Marka Dönüşüm Raporu
        </span>
      </header>
    )}
    <div className={cover ? "flex flex-1 flex-col" : "flex-1 pt-8"}>{children}</div>
    {!cover && (
      <footer className="report-avoid-break mt-8 flex items-center justify-between border-t border-[#16130f]/10 pt-3">
        <span className="h-[2px] w-8" style={{ background: COPPER }} />
        <span className="font-mono text-[9px] tracking-[0.25em] text-[#16130f]/45">
          {String(page).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </footer>
    )}
  </section>
);

/* tek satırlık fiyat tablosu satırı */
const Row = ({ label, sub, value, strike = false, badge, total = false, accent = false }) => (
  <div
    className={`report-avoid-break flex items-center justify-between gap-4 py-3.5 ${
      total ? "border-t-2" : "border-b"
    }`}
    style={{ borderColor: total ? COPPER : "rgba(22,19,15,0.08)" }}
  >
    <div className="min-w-0">
      <div className={`${total ? "font-serif text-lg" : "text-[14px]"} text-[#16130f]`}>{label}</div>
      {sub && <div className="font-mono text-[10px] uppercase tracking-wide text-[#16130f]/40">{sub}</div>}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {badge && (
        <span
          className="rounded-full px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white"
          style={{ background: COPPER }}
        >
          {badge}
        </span>
      )}
      <span
        className={`font-mono tabular-nums ${
          total ? "text-xl font-bold" : "text-[15px]"
        } ${strike ? "text-[#16130f]/35 line-through" : ""}`}
        style={{ color: total ? COPPER : accent ? COPPER : strike ? undefined : "#16130f" }}
      >
        {value}
      </span>
    </div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3 text-[13.5px] leading-relaxed text-[#2c2620]">
    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full" style={{ background: COPPER }} />
    <span>{children}</span>
  </li>
);

/* ---------- içerik verisi ---------- */
const COM_FEATURES = [
  "Full Dark Luxury — sinematik, premium tasarım dili",
  "Canlı ürün arama + dinamik gram bazlı fiyatlandırma",
  "Sofra Senaryoları (akıllı mangal hesaplayıcı)",
  "Detaylı ürün kartları, kategori ve paket sayfaları",
  "WhatsApp sipariş altyapısı (sepet/ödeme yok, doğrudan dönüşüm)",
  "Mobil öncelikli, dokunmatik optimize deneyim",
  "Yönetim paneli: ürün, hızlı fiyat güncelleme, dönüşüm analitiği",
  "SEO mimarisi: sitemap, canonical, JSON-LD yapısal veri",
];
const COMTR_FEATURES = [
  "Et işleme tesisi kurumsal kimlik platformu",
  "Üretim, hijyen ve kalite standartlarının sunumu",
  "Toptan ve kurumsal müşteri iletişim altyapısı",
  "Tedarik güvenilirliği odaklı kurumsal vitrin",
  "Markanın B2B itibarını güçlendiren içerik mimarisi",
];
const GROWTH = [
  { m: "Ay 1", t: "Temel & Görünürlük", items: ["SEO teknik altyapı + Google Search Console", "Google İşletme & yerel SEO optimizasyonu", "Meta reklam hesabı ve piksel kurulumu"] },
  { m: "Ay 2", t: "Dönüşüm & Erişim", items: ["Hedefli Meta (Instagram/Facebook) reklam kampanyaları", "İçerik takvimi + güven odaklı sosyal medya yönetimi", "WhatsApp dönüşüm optimizasyonu"] },
  { m: "Ay 3", t: "Lokal Hakimiyet", items: ["Bölgesel arama sıralamalarında üst sıralar", "Sadakat & tekrar sipariş kampanyaları", "Marka bilinirliği ve tavsiye oranında artış"] },
];

export default function Report() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Ertan-Kasap-Dijital-Marka-Donusum-Raporu";
    return () => { document.title = prev; };
  }, []);

  const TOTAL = 8;

  return (
    <>
      <Helmet>
        <html lang="tr" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="report-root">
        {/* Araç çubuğu (baskıda gizli) */}
        <div className="no-print mx-auto mb-6 flex max-w-[210mm] items-center justify-between px-4">
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#16130f]/55">
            <Lock className="h-3.5 w-3.5" /> Gizli Rapor
          </span>
          <button
            onClick={() => window.print()}
            data-testid="report-download-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition-transform hover:scale-[1.03]"
            style={{ background: "#16130f" }}
          >
            <Download className="h-4 w-4" /> PDF İndir
          </button>
        </div>

        {/* 01 — KAPAK */}
        <Sheet cover>
          <div className="flex flex-1 flex-col justify-between" data-testid="report-cover">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#16130f]/50">
                Gizli • Marka Dönüşüm Dosyası
              </span>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#16130f]/50">
                2026
              </span>
            </div>

            <div className="py-10">
              <div className="mb-6 h-[3px] w-16" style={{ background: COPPER }} />
              <div className="font-mono text-xs tracking-[0.5em] uppercase text-[#16130f]/60">
                Ertan Kasap
              </div>
              <h1 className="mt-5 font-serif text-[44px] md:text-[60px] leading-[1.02] text-[#16130f]">
                Dijital Marka
                <br />
                Dönüşüm Raporu
              </h1>
              <p className="mt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-[#16130f]/55">
                Gayrettepe • Beşiktaş • İstanbul
              </p>
              <p className="mt-10 max-w-md font-serif text-2xl italic leading-snug" style={{ color: COPPER }}>
                Bir ürün değil, bir miras.
              </p>
              <p className="mt-2 max-w-md text-[14px] leading-relaxed text-[#2c2620]">
                1985'ten beri aynı ustalık. Aynı güven.
              </p>
            </div>

            <div className="flex items-end justify-between border-t border-[#16130f]/10 pt-5">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#16130f]/40">Hazırlayan</div>
                <div className="mt-1 font-serif text-lg text-[#16130f]">Taner Torun</div>
                <div className="font-mono text-[10px] uppercase tracking-wide text-[#16130f]/50">Marka Mentörü</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#16130f]/40">Rapor No</div>
                <div className="mt-1 font-mono text-sm text-[#16130f]">EK-2026-01</div>
              </div>
            </div>
          </div>
        </Sheet>

        {/* 02 — ÖNSÖZ */}
        <Sheet page={2} total={TOTAL}>
          <Eyebrow>Önsöz</Eyebrow>
          <SectionTitle className="mt-4">Sayın Ertan Kasap Ailesi,</SectionTitle>
          <div className="mt-7 max-w-2xl space-y-5 text-[14.5px] leading-[1.85] text-[#2c2620]">
            <p>Bu çalışma yalnızca bir web sitesi geliştirme projesi değildir.</p>
            <p>
              Bu çalışma; marka algısını güçlendirmek, müşteri güvenini artırmak, sadakati yükseltmek,
              WhatsApp sipariş dönüşümlerini geliştirmek ve bölgesel marka liderliği oluşturmak amacıyla
              hazırlanmıştır.
            </p>
            <p>
              Yaklaşık 20 yıllık marka geliştirme, yazılım mimarisi, dijital pazarlama, kullanıcı
              davranışları, karar psikolojisi ve topluluk yönetimi deneyimi doğrultusunda proje
              kurgulanmıştır.
            </p>
            <p>Bu süreçte birlikte çalışmaktan memnuniyet duyduğumu belirtmek isterim.</p>
          </div>
          <div className="mt-12">
            <div className="font-serif text-2xl" style={{ color: COPPER }}>Taner Torun</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16130f]/55">Marka Mentörü</div>
          </div>
        </Sheet>

        {/* 03 — VİZYON + TAMAMLANAN VARLIKLAR */}
        <Sheet page={3} total={TOTAL}>
          <Eyebrow>Bölüm 01 — Projenin Vizyonu</Eyebrow>
          <SectionTitle className="mt-4">
            Gayrettepe ve çevresinde akla gelen <span style={{ color: COPPER }}>ilk kasap markası</span> olmak.
          </SectionTitle>
          <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-5">
            {["Güven", "Sadakat", "Marka Değeri", "WhatsApp Siparişleri", "Tavsiye Oranı"].map((h) => (
              <div key={h} className="border-t pt-3" style={{ borderColor: COPPER }}>
                <div className="font-serif text-base text-[#16130f]">{h}</div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Eyebrow>Bölüm 02 — Tamamlanan Dijital Varlıklar</Eyebrow>
            <div className="mt-5 grid gap-x-10 gap-y-0 sm:grid-cols-2">
              {[
                ["ertankasap.com", "Premium Gayrettepe Kasap Platformu"],
                ["ertankasap.com.tr", "Et İşleme Tesisi Kurumsal Platformu"],
                ["Marka Güven Merkezi", "Stratejik içerik & güven sistemi"],
                ["Instagram Marka Sistemi", "Ürün odaklıdan marka odaklıya"],
                ["WhatsApp Sipariş Altyapısı", "Sürtünmesiz dönüşüm"],
                ["SEO & Meta Reklam Altyapısı", "Görünürlük ve erişim"],
              ].map(([t, s], i) => (
                <div
                  key={t}
                  className="report-avoid-break flex items-baseline justify-between gap-4 border-b py-3.5"
                  style={{ borderColor: "rgba(22,19,15,0.08)" }}
                >
                  <div>
                    <div className="text-[14px] font-medium text-[#16130f]">{t}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wide text-[#16130f]/45">{s}</div>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: COPPER }}>
                    Tamam
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Sheet>

        {/* 04 — İKİ PLATFORM */}
        <Sheet page={4} total={TOTAL}>
          <Eyebrow>Bölüm 03 & 04 — Dijital Platformlar</Eyebrow>
          <SectionTitle className="mt-4">İki ayrı amaç, tek marka çatısı.</SectionTitle>
          <div className="mt-8 grid gap-10 md:grid-cols-2">
            <div>
              <div className="font-mono text-[11px] tracking-[0.2em]" style={{ color: COPPER }}>ertankasap.com</div>
              <div className="mt-1 font-serif text-xl text-[#16130f]">Premium Kasap Platformu</div>
              <ul className="mt-5 space-y-2.5">
                {COM_FEATURES.map((f) => <Bullet key={f}>{f}</Bullet>)}
              </ul>
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-[0.2em]" style={{ color: COPPER }}>ertankasap.com.tr</div>
              <div className="mt-1 font-serif text-xl text-[#16130f]">Kurumsal Tesis Platformu</div>
              <ul className="mt-5 space-y-2.5">
                {COMTR_FEATURES.map((f) => <Bullet key={f}>{f}</Bullet>)}
              </ul>
            </div>
          </div>
        </Sheet>

        {/* 05 — GÜVEN MERKEZİ + INSTAGRAM */}
        <Sheet page={5} total={TOTAL}>
          <Eyebrow>Bölüm 05 — Marka Güven Merkezi</Eyebrow>
          <SectionTitle className="mt-4">
            Sosyal medya paylaşımı değil, <span style={{ color: COPPER }}>güven mühendisliği.</span>
          </SectionTitle>
          <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-4">
            {[["31", "Analiz Edilmiş İçerik"], ["12", "Stratejik Analiz Katmanı"], ["10", "Psikolojik Tetikleyici"], ["1", "İçerik DNA Sistemi"]].map(([n, l]) => (
              <div key={l} className="border-t pt-3" style={{ borderColor: COPPER }}>
                <div className="font-mono text-3xl font-bold text-[#16130f]">{n}</div>
                <div className="mt-1 text-[11px] leading-snug text-[#16130f]/55">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-2xl text-[13.5px] leading-relaxed text-[#2c2620]">
            Her içerik; hook, amaç, verilen duygu, psikolojik tetikleyici, marka & sadakat katkısı ve
            hedeflenen davranış değişikliği üzerinden analiz edilir. Müşteri "Instagram postu" değil, bir
            müşteri kazanma sistemi görür.
          </p>

          <div className="mt-11">
            <Eyebrow>Bölüm 06 — Instagram Dönüşümü</Eyebrow>
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-[#16130f]/12 p-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#16130f]/45">Eski Yapı</div>
                <ul className="mt-3 space-y-2 text-[13px] text-[#2c2620]">
                  <li>Ürün ve fiyat odaklı paylaşımlar</li>
                  <li>Dağınık, stratejisiz akış</li>
                  <li>Tek seferlik satış mantığı</li>
                </ul>
              </div>
              <div className="rounded-lg p-5" style={{ background: "#16130f" }}>
                <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: COPPER }}>Yeni Yapı</div>
                <ul className="mt-3 space-y-2 text-[13px] text-white/85">
                  <li>Marka ve güven odaklı anlatım</li>
                  <li>Psikolojik kurguya dayalı içerik</li>
                  <li>Sadakat ve tekrar sipariş mantığı</li>
                </ul>
              </div>
            </div>
          </div>
        </Sheet>

        {/* 06 — BÜYÜME PLANI */}
        <Sheet page={6} total={TOTAL}>
          <Eyebrow>Bölüm 07 — 3 Aylık Büyüme Planı</Eyebrow>
          <SectionTitle className="mt-4">Görünürlükten lokal hakimiyete.</SectionTitle>
          <div className="mt-8 space-y-5">
            {GROWTH.map((g) => (
              <div key={g.m} className="report-avoid-break grid gap-4 border-l-2 pl-5 sm:grid-cols-[120px_1fr]" style={{ borderColor: COPPER }}>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: COPPER }}>{g.m}</div>
                  <div className="font-serif text-lg text-[#16130f]">{g.t}</div>
                </div>
                <ul className="space-y-2 self-center">
                  {g.items.map((it) => <Bullet key={it}>{it}</Bullet>)}
                </ul>
              </div>
            ))}
          </div>
        </Sheet>

        {/* 07 — YATIRIM / FİNANSAL */}
        <Sheet page={7} total={TOTAL}>
          <Eyebrow>Bölüm 08–10 — Yatırım Özeti</Eyebrow>
          <SectionTitle className="mt-4">Proje değeri ve markaya özel destekler.</SectionTitle>

          <div className="mt-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16130f]/45">Tamamlanan Çalışmalar</div>
            <div className="mt-1">
              <Row label="ertankasap.com" sub="Premium kasap platformu" value="100.000 ₺" />
              <Row label="ertankasap.com.tr" sub="Kurumsal tesis platformu" value="100.000 ₺" />
              <Row label="3 Aylık SEO" sub="Teknik + içerik + yerel" value="120.000 ₺" />
              <Row label="3 Aylık Sosyal Medya" sub="İçerik + reklam yönetimi" value="120.000 ₺" />
              <Row label="Hosting ve Alan Adı (.com)" value="12.000 ₺" />
              <Row label="Hosting ve Alan Adı (.com.tr)" value="12.000 ₺" />
              <Row label="Toplam Proje Değeri" value="464.000 ₺" total />
            </div>
          </div>

          <div className="mt-9">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16130f]/45">Ertan Kasap'a Özel Destekler</div>
            <div className="mt-1">
              <Row label="3 Aylık SEO" value="120.000 ₺" strike badge="Hediye" />
              <Row label="3 Aylık Sosyal Medya" value="120.000 ₺" strike badge="Hediye" />
              <Row label="Toplam Destek" value="− 240.000 ₺" total />
            </div>
          </div>
        </Sheet>

        {/* 08 — NET YATIRIM + KAPANIŞ */}
        <Sheet page={8} total={TOTAL}>
          <Eyebrow>Net Yatırım</Eyebrow>
          <SectionTitle className="mt-4">Şeffaf finansal özet.</SectionTitle>
          <div className="mt-7 rounded-xl border border-[#16130f]/12 p-6">
            <Row label="Toplam Proje Değeri" value="464.000 ₺" />
            <Row label="Özel Destekler" value="− 240.000 ₺" accent />
            <Row label="Net Yatırım" value="224.000 ₺" />
            <Row label="KDV %20" value="44.800 ₺" />
            <Row label="Genel Toplam" value="268.800 ₺" total />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-wide text-[#16130f]/45">
            464.000 ₺ değerinde tamamlanmış dijital marka dönüşümü · markaya özel 240.000 ₺ destek
          </p>

          <div className="mt-auto pt-14">
            <div className="h-[3px] w-16" style={{ background: COPPER }} />
            <h2 className="mt-6 font-serif text-[34px] leading-[1.05] text-[#16130f]">
              Bir ürün değil, <span style={{ color: COPPER }}>bir miras.</span>
            </h2>
            <p className="mt-3 text-[13.5px] leading-relaxed text-[#2c2620]">
              1985'ten beri aynı ustalık. Aynı titizlik. Aynı güven.
            </p>
            <div className="mt-8 flex items-end justify-between border-t border-[#16130f]/10 pt-5">
              <div>
                <div className="font-serif text-lg text-[#16130f]">Taner Torun</div>
                <div className="font-mono text-[10px] uppercase tracking-wide text-[#16130f]/50">Marka Mentörü</div>
              </div>
              <div className="text-right font-mono text-[10px] uppercase tracking-wide text-[#16130f]/50">
                Ertan Kasap · Gayrettepe / Beşiktaş
              </div>
            </div>
          </div>
        </Sheet>
      </div>
    </>
  );
}
